// The clock behind every transient panel (coachmarks, offer toasts): shown for
// TRANSIENT_MS, fading for the last FADE_MS, then `onGone` fires and the owner
// unmounts it.
//
// The clock *pauses* while the pointer is over the panel or keyboard focus is
// inside it — a card someone is reading or tabbing through must not vanish
// under them. Leaving resumes from where it stopped. Under
// prefers-reduced-motion there is no fade: the panel simply goes at the
// deadline.
//
// `onGone` only means "stop showing this". What the panel *was* (an offer, a
// hint) stays wherever it lives — the toast is a notification, never the
// record.
import { type FocusEvent, useEffect, useRef, useState } from 'react';

/** How long a transient panel stays up, fade included. */
export const TRANSIENT_MS = 10_000;
/** The last stretch of TRANSIENT_MS spent fading out. */
export const FADE_MS = 600;
/** What a panel gets back after being held mid-fade, before it fades again. */
const RESUME_GRACE_MS = 2_000;

export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

export type DismissPhase = 'shown' | 'fading';

export interface AutoDismiss {
  readonly phase: DismissPhase;
  /** Length of the fade in ms; 0 when motion is reduced (no transition). */
  readonly fadeMs: number;
  /** Spread on the panel's root: hover and focus pause the clock. */
  readonly hold: {
    readonly onMouseEnter: () => void;
    readonly onMouseLeave: () => void;
    readonly onFocus: (e: FocusEvent<HTMLElement>) => void;
    readonly onBlur: (e: FocusEvent<HTMLElement>) => void;
  };
}

export interface AutoDismissOptions {
  readonly ttlMs?: number;
  readonly fadeMs?: number;
  /** False keeps the panel up until its owner removes it. */
  readonly enabled?: boolean;
}

interface Clock {
  elapsed: number;
  startedAt: number;
  running: boolean;
  fading: boolean;
  hovered: boolean;
  focused: boolean;
  timer: ReturnType<typeof setTimeout> | null;
}

function keyboardFocus(target: EventTarget): boolean {
  try {
    return (target as Element).matches(':focus-visible');
  } catch {
    return true;
  }
}

export function useAutoDismiss(
  onGone: () => void,
  { ttlMs = TRANSIENT_MS, fadeMs = FADE_MS, enabled = true }: AutoDismissOptions = {},
): AutoDismiss {
  const [phase, setPhase] = useState<DismissPhase>('shown');
  const gone = useRef(onGone);
  gone.current = onGone;
  const reduced = prefersReducedMotion();
  const fade = reduced ? 0 : Math.min(fadeMs, ttlMs);
  const clock = useRef<Clock>({
    elapsed: 0,
    startedAt: 0,
    running: false,
    fading: false,
    hovered: false,
    focused: false,
    timer: null,
  });
  const live = useRef({ ttlMs, fade, enabled });
  live.current = { ttlMs, fade, enabled };

  // Stable across renders: everything it reads lives in refs.
  const api = useRef({
    stop() {
      const c = clock.current;
      if (c.timer) clearTimeout(c.timer);
      c.timer = null;
    },
    start() {
      const c = clock.current;
      const { ttlMs: ttl, fade: f, enabled: on } = live.current;
      api.current.stop();
      if (!on) return;
      c.running = true;
      c.startedAt = Date.now();
      const untilFade = Math.max(0, ttl - f - c.elapsed);
      const finish = () => {
        c.timer = null;
        c.running = false;
        gone.current();
      };
      c.timer = setTimeout(() => {
        if (f === 0) return finish();
        c.fading = true;
        setPhase('fading');
        c.timer = setTimeout(finish, f);
      }, untilFade);
    },
    sync() {
      const c = clock.current;
      const { ttlMs: ttl, fade: f } = live.current;
      if (c.hovered || c.focused) {
        if (!c.running) return;
        api.current.stop();
        c.running = false;
        if (c.fading) {
          c.fading = false;
          c.elapsed = Math.max(0, ttl - f - RESUME_GRACE_MS);
          setPhase('shown');
        } else {
          c.elapsed += Date.now() - c.startedAt;
        }
      } else if (!c.running) {
        api.current.start();
      }
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: the clock lives in refs; only a change of ttl/enabled restarts it.
  useEffect(() => {
    const a = api.current;
    const c = clock.current;
    c.elapsed = 0;
    c.fading = false;
    c.running = false;
    setPhase('shown');
    if (!c.hovered && !c.focused) a.start();
    return a.stop;
  }, [ttlMs, enabled]);

  const hold = useRef<AutoDismiss['hold']>({
    onMouseEnter: () => {
      clock.current.hovered = true;
      api.current.sync();
    },
    onMouseLeave: () => {
      clock.current.hovered = false;
      api.current.sync();
    },
    onFocus: (e) => {
      if (!keyboardFocus(e.target)) return;
      clock.current.focused = true;
      api.current.sync();
    },
    onBlur: (e) => {
      const next = e.relatedTarget as Node | null;
      if (next && e.currentTarget.contains(next)) return;
      clock.current.focused = false;
      api.current.sync();
    },
  });

  return { phase, fadeMs: fade, hold: hold.current };
}
